/**
 * TimelineExportMenu Component
 *
 * Dropdown menu for exporting timeline to various formats.
 *
 * @module components/timeline/TimelineExportMenu
 */

import React, { useState } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import {
  exportToYAML,
  exportToMarkdown,
  exportToHTML,
  exportToPDF,
} from '../../utils/timelineExport';
import { toast } from '../../utils/toast';

/**
 * TimelineExportMenu component
 * @param {Object} props
 * @param {Object} props.project - Project data
 * @param {Array} props.events - Timeline events
 * @param {boolean} props.disabled - Whether export is disabled
 */
const TimelineExportMenu = ({ project, events, disabled = false }) => {
  const [exporting, setExporting] = useState(null);

  const handleExport = async (format) => {
    if (exporting) return;

    setExporting(format);

    try {
      const exportData = {
        project: {
          id: project?.id,
          number: project?.project_number,
          title: project?.title,
          status: project?.status,
          client: project?.client?.company_name || project?.client?.primary_contact_name,
        },
        events,
        exportDate: new Date().toISOString(),
      };

      const filename = `bitacora_${project?.project_number || 'proyecto'}_${new Date().toISOString().split('T')[0]}`;

      switch (format) {
        case 'yaml':
          exportToYAML(exportData, filename);
          toast.success('Exportado a YAML');
          break;
        case 'markdown':
          exportToMarkdown(exportData, filename);
          toast.success('Exportado a Markdown');
          break;
        case 'html':
          exportToHTML(exportData, filename);
          toast.success('Exportado a HTML');
          break;
        case 'pdf':
          await exportToPDF(exportData, filename);
          toast.success('Exportado a PDF');
          break;
        default:
          throw new Error('Formato desconocido');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Error al exportar: ${error.message}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <Dropdown>
      <Dropdown.Toggle
        variant="outline-secondary"
        size="sm"
        disabled={disabled || !events?.length}
        id="timeline-export-dropdown"
      >
        {exporting ? (
          <>
            <Spinner size="sm" animation="border" className="me-1" />
            Exportando...
          </>
        ) : (
          <>
            <i className="bi bi-download me-1"></i>
            Exportar
          </>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu align="end">
        <Dropdown.Header>AI-Ready</Dropdown.Header>
        <Dropdown.Item onClick={() => handleExport('yaml')} disabled={!!exporting}>
          <i className="bi bi-filetype-yml me-2"></i>
          YAML
          <small className="text-muted ms-2">(para IA)</small>
        </Dropdown.Item>
        <Dropdown.Item onClick={() => handleExport('markdown')} disabled={!!exporting}>
          <i className="bi bi-markdown me-2"></i>
          Markdown
          <small className="text-muted ms-2">(para IA)</small>
        </Dropdown.Item>

        <Dropdown.Divider />

        <Dropdown.Header>Presentable</Dropdown.Header>
        <Dropdown.Item onClick={() => handleExport('html')} disabled={!!exporting}>
          <i className="bi bi-filetype-html me-2"></i>
          HTML
          <small className="text-muted ms-2">(web/imprimir)</small>
        </Dropdown.Item>
        <Dropdown.Item onClick={() => handleExport('pdf')} disabled={!!exporting}>
          <i className="bi bi-filetype-pdf me-2"></i>
          PDF
          <small className="text-muted ms-2">(documento)</small>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default TimelineExportMenu;
